import { useEffect, useRef, useState } from 'react';
import './App.css';
import Header from './Header';

const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

const scrollToContact = (e) => {
  e.preventDefault();
  document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
};

function App() {
  const [darkHeader, setDarkHeader] = useState(false);
  const sectionTwoRef = useRef(null);
  const sectionFourRef = useRef(null);

  useEffect(() => {
    if (window.location.hash) {
      const el = document.getElementById(window.location.hash.slice(1));
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  useEffect(() => {
    // Switch the header style based on which section is under it: only a thin
    // strip behind the header counts, so sections of any height work. The strip
    // starts below y=0 because a section ending exactly at the top edge would
    // otherwise still count as intersecting.
    const visibleSections = new Set();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) visibleSections.add(entry.target);
          else visibleSections.delete(entry.target);
        });
        setDarkHeader(visibleSections.size > 0);
      },
      { rootMargin: '-5% 0px -90% 0px' }
    );
    if (sectionTwoRef.current) observer.observe(sectionTwoRef.current);
    if (sectionFourRef.current) observer.observe(sectionFourRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="App">
      <Header dark={darkHeader} />

      <section id="home" className="section-hero">
        <video
          className="hero-video"
          src="/images/hero.mp4"
          poster="/images/hero_poster.jpg"
          autoPlay={!prefersReducedMotion}
          preload={prefersReducedMotion ? 'none' : 'auto'}
          loop
          muted
          playsInline
          aria-hidden="true"
        />
        <div className="hero-overlay" />
        <div className="hero-content">
          <h1 className="hero-title">Data Matters</h1>
          <p className="hero-subtitle">Highest Quality DNA and RNA Extractions</p>
          <a href="#contact" className="hero-cta" onClick={scrollToContact}>Request a Demo</a>
        </div>
        <div className="hero-backers-bar">
          <span className="hero-backers-label">Backed by</span>
          <img src="/backers/NIH.png" alt="NIH" className="backer-logo-nih" width="256" height="40" />
          <img src="/backers/QB3.webp" alt="QB3" className="backer-logo-qb3" width="200" height="200" />
          <img src="/backers/codonLogo.webp" alt="Codon" className="backer-logo-codon" width="206" height="200" />
          <img src="/backers/Vivo.png" alt="Vivo" className="backer-logo-vivo" width="285" height="93" />
        </div>
      </section>

      <section id="section-two" className="section-two" ref={sectionTwoRef}>
        <div className="section-two-inner">
            <div className="top-block">
                <img src="/images/product_image.webp" alt="Vellum extraction instrument" className="product-image" width="1100" height="947" loading="lazy" />
                <div className="section-text">
                    <h2 className="section-title">Fully automatable extraction using Epitachophoresis</h2>
                    <p className="section-description-2">No Centrifuge. No Magnets.</p>
                    <p className="section-description-2">PCR Ready, Large Insert Size DNA.</p>
                    <p className="section-description-2">Ribosomal Depletion Ready RNA.</p>
                    <p className="section-description-2">Tissue Specific Extraction Protocols.</p>
                </div>
            </div>
            <div className="workflow-block">
                <span className="workflow-label">Workflow</span>
                <div className="workflow-steps">
                    <div className="workflow-step">
                        <img src="/images/digest.webp" alt="" className="workflow-step-image" width="320" height="320" loading="lazy" />
                        <span className="workflow-step-label">1. Digestion</span>
                    </div>
                    <div className="workflow-arrow" aria-hidden="true">→</div>
                    <div className="workflow-step">
                        <img src="/images/ETP.webp" alt="" className="workflow-step-image" width="320" height="303" loading="lazy" />
                        <span className="workflow-step-label">2. ETP</span>
                    </div>
                    <div className="workflow-arrow" aria-hidden="true">→</div>
                    <div className="workflow-step">
                        <img src="/images/collection.webp" alt="" className="workflow-step-image" width="320" height="320" loading="lazy" />
                        <span className="workflow-step-label">3. Collection</span>
                    </div>
                </div>
            </div>
        </div>
      </section>

      <section className="section-three" id="section-three">
        <div className="section-three-inner">
          <div className="section-three-text">
            <h2>Built for Scale</h2>
            <p className="section-description">Vellum's platform is engineered around standard laboratory formats, enabling seamless integration into OEM liquid-handling workflows.</p>
            <p className="section-description">Process up to 12 samples simultaneously with consistent, high-yield extractions across every well — no manual intervention required.</p>
            <a href="#contact" className="section-three-cta" onClick={scrollToContact}>Get in Touch</a>
          </div>
          <img src="/images/12_well_plate.webp" alt="12-well plate" className="well-plate-image" width="976" height="906" loading="lazy" />
        </div>
      </section>

      <section className="section-four" id="contact" ref={sectionFourRef}>
        <div className="section-four-inner">
          <h2>Get In Touch</h2>
          <p className="contact-blurb">Interested in a demo, evaluation, or partnership? Send us a note.</p>
          <a className="contact-cta" href="mailto:info@vellumbiotechnology.com?subject=Demo%20request">
            Request a Demo
          </a>
          <a className="contact-email" href="mailto:info@vellumbiotechnology.com">
            info@vellumbiotechnology.com
          </a>
        </div>
        <footer className="site-footer">
          <a className="footer-link" href="https://drive.google.com/file/d/1QH6-zi9GqcelccOjRLWb_29vPrj8WSNv/view" target="_blank" rel="noopener noreferrer">
            FCOI Policy
          </a>
          <a className="footer-link" href="https://www.linkedin.com/company/vellum-biotechnology/posts/?feedView=all" target="_blank" rel="noopener noreferrer">
            <img src="/images/linkedin_icon_64.png" alt="" className="linkedin-icon" width="64" height="64" />
            LinkedIn
          </a>
        </footer>
      </section>
    </div>
  );
}

export default App;
